export interface FileDiffParsed {
  path: string
  oldPath: string
  newPath: string
  rows: DiffRowParsed[]
}

export type DiffRowParsed =
  | { kind: 'hunk-header'; text: string }
  | { kind: 'context'; leftNum: number; rightNum: number; content: string }
  | { kind: 'del'; leftNum: number; content: string }
  | { kind: 'add'; rightNum: number; content: string }
  | { kind: 'pair'; leftNum: number; rightNum: number; leftContent: string; rightContent: string }

export function parseDiff(diffText: string): FileDiffParsed[] {
  const files: FileDiffParsed[] = []
  if (!diffText.trim()) return files

  // Split on `diff --git` boundaries
  const sections = diffText.split(/(?=^diff --git )/m)

  for (const section of sections) {
    if (!section.trim()) continue

    const lines = section.split('\n')
    let oldPath = ''
    let newPath = ''
    const rows: DiffRowParsed[] = []
    let i = 0
    let leftNum = 0
    let rightNum = 0

    while (i < lines.length) {
      const line = lines[i]

      if (line.startsWith('--- ')) {
        oldPath = line.slice(4).replace(/^a\//, '').trim()
        i++
      } else if (line.startsWith('+++ ')) {
        newPath = line.slice(4).replace(/^b\//, '').trim()
        i++
      } else if (line.startsWith('@@ ')) {
        const m = line.match(/@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/)
        if (m) {
          leftNum = parseInt(m[1], 10) - 1
          rightNum = parseInt(m[2], 10) - 1
        }
        rows.push({ kind: 'hunk-header', text: line })
        i++

        const delBuf: Array<{ num: number; content: string }> = []
        const addBuf: Array<{ num: number; content: string }> = []

        const flush = () => {
          const max = Math.max(delBuf.length, addBuf.length)
          for (let j = 0; j < max; j++) {
            const d = delBuf[j]
            const a = addBuf[j]
            if (d && a) {
              rows.push({ kind: 'pair', leftNum: d.num, rightNum: a.num, leftContent: d.content, rightContent: a.content })
            } else if (d) {
              rows.push({ kind: 'del', leftNum: d.num, content: d.content })
            } else if (a) {
              rows.push({ kind: 'add', rightNum: a.num, content: a.content })
            }
          }
          delBuf.length = 0
          addBuf.length = 0
        }

        while (i < lines.length) {
          const hl = lines[i]
          if (hl.startsWith('@@ ')) {
            flush()
            const m2 = hl.match(/@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/)
            if (m2) {
              leftNum = parseInt(m2[1], 10) - 1
              rightNum = parseInt(m2[2], 10) - 1
            }
            rows.push({ kind: 'hunk-header', text: hl })
            i++
          } else if (hl.startsWith('-') && !hl.startsWith('---')) {
            leftNum++
            delBuf.push({ num: leftNum, content: hl.slice(1) })
            i++
          } else if (hl.startsWith('+') && !hl.startsWith('+++')) {
            rightNum++
            addBuf.push({ num: rightNum, content: hl.slice(1) })
            i++
          } else if (hl.startsWith(' ') || hl === '') {
            flush()
            leftNum++
            rightNum++
            rows.push({ kind: 'context', leftNum, rightNum, content: hl.length > 0 ? hl.slice(1) : '' })
            i++
          } else if (hl.startsWith('diff --git ') || hl.startsWith('\\ No newline')) {
            break
          } else {
            i++
          }
        }
        flush()
      } else {
        i++
      }
    }

    if (newPath || oldPath) {
      files.push({ path: newPath || oldPath, oldPath, newPath, rows })
    }
  }

  return files
}
